import { Certif } from '../CertificatesPage';
import { LabExam } from '../LabExam';
import { Declaration, DeclarationBasis } from '../types/declaration';
import { PdfCounter, PdfSection, PdfSummaryCard, RegistryPdfDocument } from '../types/registryPdf';
import { buildCertificateRegistryCard, buildCommentCard, buildVoluntaryCertificateRegistryCard } from './certificatesPdfUtils';
import { buildDeclarationRegistryCard, buildDeclarationStatusCard } from './declarationsPdfUtils';
import { getLegalBasis } from './declarationUtils';
import { buildExamResultsCard, buildLabExamRegistryCard } from './labExamsPdfUtils';

interface ExaminationPdfParams {
    organizationName: string;
    inn: string;
    address: string;
    labExams: LabExam[];
    weldedJointExams: LabExam[];
    certificates: Certif[];
    voluntaryCertificates: Certif[];
    declarationsByBasis: { basis: DeclarationBasis; basisDeclarations: Declaration[] }[];
}


const getFileName = (organizationName: string, inn: string): string => {
    const safeName = (organizationName || 'Неизвестная_компания').replace(/[<>:"/\\|?*]/g, '_').trim();
    return `Комплексная_проверка_${safeName}_${inn || 'без_ИНН'}.pdf`;
};


// В отчёт попадают те же блоки, что на странице проверки, и только если по ним есть данные
export const buildExaminationDocument = (params: ExaminationPdfParams): RegistryPdfDocument => {
    const declarationGroups = params.declarationsByBasis.filter(group => group.basisDeclarations.length > 0);

    // Счётчики как в блоках "Сертификаты" и "Декларации" на странице
    const counters: PdfCounter[] = [];
    const certificatesCount = params.certificates.length + params.voluntaryCertificates.length;
    if (certificatesCount > 0) {
        counters.push({ label: 'Сертификаты, всего', value: certificatesCount });
        counters.push({ label: 'Обязательные сертификаты', value: params.certificates.length });
        counters.push({ label: 'Добровольные сертификаты', value: params.voluntaryCertificates.length });
    }
    if (declarationGroups.length > 0) {
        const declarationsCount = declarationGroups.reduce((sum, group) => sum + group.basisDeclarations.length, 0);
        counters.push({ label: 'Декларации, всего', value: declarationsCount });
        declarationGroups.forEach(group => {
            counters.push({ label: `Декларации ${getLegalBasis(group.basis)}`, value: group.basisDeclarations.length });
        });
    }
    if (params.weldedJointExams.length > 0) {
        counters.push({ label: 'Испытания сварных соединений', value: params.weldedJointExams.length });
    }

    const cards: PdfSummaryCard[] = [];
    const sections: PdfSection[] = [];

    if (params.labExams.length > 0) {
        cards.push(buildExamResultsCard('Лабораторные испытания (результаты)', params.labExams));
        sections.push({ kind: 'cards', title: 'Лабораторные испытания', cards: params.labExams.map(buildLabExamRegistryCard) });
    }
    if (params.weldedJointExams.length > 0) {
        cards.push(buildExamResultsCard('Испытания сварных соединений (результаты)', params.weldedJointExams));
        sections.push({
            kind: 'cards',
            title: 'Испытания сварных соединений',
            cards: params.weldedJointExams.map(buildLabExamRegistryCard),
        });
    }
    if (params.certificates.length > 0) {
        cards.push(buildCommentCard('Обязательные сертификаты (АПТС)', params.certificates, true));
        sections.push({ kind: 'cards', title: 'Обязательные сертификаты', cards: params.certificates.map(buildCertificateRegistryCard) });
    }
    if (params.voluntaryCertificates.length > 0) {
        cards.push(buildCommentCard('Добровольные сертификаты (АПТС)', params.voluntaryCertificates, true));
        sections.push({ kind: 'cards', title: 'Добровольные сертификаты', cards: params.voluntaryCertificates.map(buildVoluntaryCertificateRegistryCard) });
    }
    declarationGroups.forEach(group => {
        const basisLabel = getLegalBasis(group.basis);
        cards.push(buildDeclarationStatusCard(`Декларации ${basisLabel}`, group.basisDeclarations));
        sections.push({ kind: 'cards', title: `Декларации ${basisLabel}`, cards: group.basisDeclarations.map(buildDeclarationRegistryCard) });
    });

    const subtitleParts: string[] = [];
    if (params.organizationName) subtitleParts.push(params.organizationName);
    if (params.inn) subtitleParts.push(`ИНН ${params.inn}`);
    if (params.address && params.address !== 'Нет данных') subtitleParts.push(params.address);

    return {
        fileName: getFileName(params.organizationName, params.inn),
        summary: {
            title: 'Комплексная проверка организации',
            subtitle: subtitleParts.join(' · '),
            counters,
            cards,
        },
        sections,
    };
};
